import { useEffect, useState } from "react";
import { Button, Container, Dropdown, Form, Grid, Header, Icon, List, Menu, MenuItem, Message, Placeholder, Segment, Statistic } from "semantic-ui-react";
import {
    LineChart,
    Line,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";
import { fetchWithAuth, getCSRFToken } from "../auth";
import { getTeams } from "../common/apiClient";
import { Link } from "react-router-dom";
import "./ReportingDashboard.css";

function downloadBlob(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const a = Object.assign(document.createElement("a"), { href: url, download: filename });
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
}

function toISOStringWithTimezone(date) {
    const tzoffset = new Date().getTimezoneOffset() * 60000;
    return new Date(date - tzoffset).toISOString().slice(0, -1);
}

const presetFilters = [
    { key: "monthOverMonth", label: "Month over Month" },
    { key: "yearToDate", label: "Year to Date" },
    { key: "last30Days", label: "Last 30 Days" },
];
const defaultPreset = "last30Days";
const topListPlaceholderCount = 10;

function formatCount(value) {
    const number = Number(value);

    if (!Number.isFinite(number)) return "0";

    return number.toLocaleString("en-US", { maximumFractionDigits: 1 });
}

function formatCurrency(value) {
    const number = Number(value);

    if (!Number.isFinite(number)) return "$0.00";

    return number.toLocaleString("en-US", { currency: "USD", style: "currency" });
}

function formatDuration(seconds) {
    const number = Number(seconds);

    if (!Number.isFinite(number) || number <= 0) return "0m";

    const totalMinutes = Math.round(number / 60);
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;

    if (days && hours) return `${days}d ${hours}h`;
    if (days) return `${days}d`;
    if (hours && minutes) return `${hours}h ${minutes}m`;
    if (hours) return `${hours}h`;

    return `${minutes}m`;
}

function getAverageTurnover(teams) {
    if (!teams.length) return 0;

    const total = teams.reduce((sum, team) => sum + (Number(team.deactivatedCount) || 0), 0);

    return total / teams.length;
}

function AssetList({ items }) {
    if (!items.length) {
        return (
            <List relaxed className="reporting-dashboard__asset-list">
                <List.Item>No data available.</List.Item>
            </List>
        );
    }

    return (
        <List divided relaxed className="reporting-dashboard__asset-list">
            {items.map((item, idx) => (
                <List.Item key={item.iccName}>
                    <List.Content floated="right" className="reporting-dashboard__asset-value">
                        <strong>{item.value}</strong>
                    </List.Content>
                    <List.Content>
                        <span className="reporting-dashboard__asset-rank">{idx + 1}.</span>
                        <Link to={"/assets/" + item.id + "/view"}>{item.iccName}</Link>
                    </List.Content>
                </List.Item>
            ))}
        </List>
    );
}

function TeamList({ items }) {
    if (!items.length) {
        return (
            <List relaxed className="reporting-dashboard__asset-list">
                <List.Item>No data available.</List.Item>
            </List>
        );
    }

    return (
        <List divided relaxed className="reporting-dashboard__asset-list">
            {items.map((item, idx) => (
                <List.Item key={item.teamName}>
                    <List.Content floated="right" className="reporting-dashboard__asset-value">
                        <strong>{item.value}</strong>
                    </List.Content>
                    <List.Content>
                        <span className="reporting-dashboard__asset-rank">{idx + 1}.</span>
                        <Link to={"/teams/" + item.id + "/view"}>{item.teamName}</Link>
                    </List.Content>
                </List.Item>
            ))}
        </List>
    );
}

function ReadinessRankingList({ items, type }) {
    if (!items.length) {
        return (
            <List relaxed className="reporting-dashboard__asset-list">
                <List.Item>No data available.</List.Item>
            </List>
        );
    }
    const basePath = type === "team" ? "/teams/" : type === "asset" ? "/assets/" : "/members/";
    return (
        <List divided relaxed className="reporting-dashboard__asset-list">
            {items.map((item, idx) => (
                <List.Item key={item.id}>
                    <List.Content floated="right" className="reporting-dashboard__asset-value">
                        <strong>{item.value}</strong>
                    </List.Content>
                    <List.Content>
                        <span className="reporting-dashboard__asset-rank">{idx + 1}.</span>
                        <Link to={basePath + item.id + "/view"}>{item.name}</Link>
                    </List.Content>
                </List.Item>
            ))}
        </List>
    );
}

function StatisticPlaceholder({ count }) {
    return (
        <Grid columns={count}>
            <Grid.Row>
                {Array.from({ length: count }, (_, idx) => (
                    <Grid.Column key={idx}>
                        <Placeholder fluid className="reporting-dashboard__stat-placeholder">
                            <Placeholder.Image />
                        </Placeholder>
                    </Grid.Column>
                ))}
            </Grid.Row>
        </Grid>
    );
}

function AssetListPlaceholder({ count }) {
    return (
        <Placeholder fluid>
            {Array.from({ length: count }, (_, idx) => (
                <Placeholder.Line key={idx} length={idx % 2 === 0 ? "full" : "very long"} />
            ))}
        </Placeholder>
    );
}

function formatDateForInput(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
}

function getPresetRange(preset) {
    const today = new Date();
    let start = today;

    if (preset === "monthOverMonth") {
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    }
    if (preset === "yearToDate") {
        start = new Date(today.getFullYear(), 0, 1);
    }
    if (preset === "last30Days") {
        start = new Date(today);
        start.setDate(today.getDate() - 29);
    }

    return {
        startDate: formatDateForInput(start),
        endDate: formatDateForInput(today),
    };
}

export function ReportingDashboard() {
    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState(null);
    const [error, setError] = useState(null);
    const [startDate, setStartDate] = useState(() => getPresetRange(defaultPreset).startDate);
    const [endDate, setEndDate] = useState(() => getPresetRange(defaultPreset).endDate);
    const [activePreset, setActivePreset] = useState(defaultPreset);
    const [readinessType, setReadinessType] = useState("team");
    const [teamReadinessLoading, setTeamReadinessLoading] = useState(true);
    const [teamReadinessTrend, setTeamReadinessTrend] = useState([]);
    const [teamReadinessRanking, setTeamReadinessRanking] = useState([]);
    const [exporting, setExporting] = useState(false);
    const [selectedTeamIds, setSelectedTeamIds] = useState([]);
    const [teamOptions, setTeamOptions] = useState([]);
    const [teamsLoading, setTeamsLoading] = useState(false);

    useEffect(() => {
        let ignore = false;

        async function loadTeams() {
            setTeamsLoading(true);
            try {
                const teams = await getTeams();
                if (!ignore) {
                    setTeamOptions((teams || []).map((t) => ({ key: t.id, value: t.id, text: t.name })));
                }
            } catch (err) {
                // Non-critical; team filter is optional.
            } finally {
                if (!ignore) setTeamsLoading(false);
            }
        }

        loadTeams();

        return () => {
            ignore = true;
        };
    }, []);

    useEffect(() => {
        let ignore = false;

        async function fetchTeamReadiness() {
            if (!startDate || !endDate) {
                setTeamReadinessTrend([]);
                setTeamReadinessRanking([]);
                setTeamReadinessLoading(false);
                return;
            }

            setTeamReadinessLoading(true);

            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);

            try {
                const resp = await fetchWithAuth(
                    process.env.REACT_APP_ICC_TOOL_API_HOST +
                        "readiness/" +
                        readinessType +
                        "?start=" +
                        start.toISOString() +
                        "&end=" +
                        end.toISOString(),
                    {
                        headers: { "X-CSRFToken": getCSRFToken() },
                        credentials: "include",
                    }
                );

                if (!resp.ok) throw new Error("Failed to load team readiness data.");

                const fullReport = await resp.json();

                // Dedupe: keep one report per team per date (latest)
                const dedupedReport = {};
                for (const id in fullReport) {
                    const lastDateForId = {};
                    dedupedReport[id] = fullReport[id]
                        .slice()
                        .reverse()
                        .filter((value) => {
                            const reportDate = toISOStringWithTimezone(new Date(value.creationTime)).split("T")[0];
                            if (reportDate !== lastDateForId[id]) {
                                lastDateForId[id] = reportDate;
                                return true;
                            }
                            return false;
                        });
                    if (!dedupedReport[id].length) delete dedupedReport[id];
                }

                // Build date -> {ready, notReady} for trend
                const dateToReady = {};
                for (const id in dedupedReport) {
                    for (const report of dedupedReport[id]) {
                        const d = toISOStringWithTimezone(new Date(report.creationTime)).split("T")[0];
                        if (!(d in dateToReady)) dateToReady[d] = { date: d, ready: 0, notReady: 0 };
                        if (report.status === "ready") dateToReady[d].ready++;
                        else dateToReady[d].notReady++;
                    }
                }
                const trend = Object.keys(dateToReady)
                    .sort()
                    .map((d) => ({
                        date: new Date(d).getTime(),
                        Ready: dateToReady[d].ready,
                        "Not ready": dateToReady[d].notReady,
                    }));

                // Compute ready% per team
                const ranking = Object.keys(dedupedReport)
                    .map((id) => {
                        let readyCount = 0;
                        let notReadyCount = 0;
                        for (const r of dedupedReport[id]) {
                            if (r.status === "ready") readyCount++;
                            else notReadyCount++;
                        }
                        const total = readyCount + notReadyCount;
                        return {
                            id,
                            teamName: dedupedReport[id][0]?.info?.name,
                            name: dedupedReport[id][0]?.info?.name,
                            readyCount,
                            notReadyCount,
                            readyPercent: total ? readyCount / total : 0,
                        };
                    })
                    .sort((a, b) => a.readyPercent - b.readyPercent);

                if (!ignore) {
                    setTeamReadinessTrend(trend);
                    setTeamReadinessRanking(ranking);
                }
            } catch (err) {
                if (!ignore) {
                    setTeamReadinessTrend([]);
                    setTeamReadinessRanking([]);
                }
            } finally {
                if (!ignore) setTeamReadinessLoading(false);
            }
        }

        fetchTeamReadiness();

        return () => {
            ignore = true;
        };
    }, [startDate, endDate, readinessType]);

    useEffect(() => {
        let ignore = false;

        async function fetchDashboardData() {
            if (!startDate || !endDate) {
                setDashboardData(null);
                setError(null);
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);

            const params = new URLSearchParams({
                start_date: startDate,
                end_date: endDate,
            });
            if (selectedTeamIds.length > 0) {
                params.set("team_ids", selectedTeamIds.join(","));
            }

            try {
                const response = await fetchWithAuth(
                    process.env.REACT_APP_ICC_TOOL_API_HOST + "reporting/dashboard/?" + params.toString()
                );

                if (!response.ok) {
                    throw new Error("Failed to load reporting dashboard data.");
                }

                const data = await response.json();

                if (!ignore) {
                    setDashboardData(data);
                }
            } catch (err) {
                if (!ignore) {
                    setDashboardData(null);
                    setError(err.message || "Failed to load reporting dashboard data.");
                }
            } finally {
                if (!ignore) {
                    setLoading(false);
                }
            }
        }

        fetchDashboardData();

        return () => {
            ignore = true;
        };
    }, [startDate, endDate, selectedTeamIds]);

    const handlePresetClick = (preset) => {
        const range = getPresetRange(preset);

        setStartDate(range.startDate);
        setEndDate(range.endDate);
        setActivePreset(preset);
    };

    const handleExport = async () => {
        setExporting(true);
        setError(null);

        const params = new URLSearchParams({
            start_date: startDate,
            end_date: endDate,
        });
        if (selectedTeamIds.length > 0) {
            params.set("team_ids", selectedTeamIds.join(","));
        }

        try {
            const response = await fetchWithAuth(
                process.env.REACT_APP_ICC_TOOL_API_HOST + "reporting/dashboard/export/?" + params.toString(),
                {
                    headers: { "X-CSRFToken": getCSRFToken() },
                    credentials: "include",
                }
            );

            if (!response.ok) {
                throw new Error("Failed to export reporting dashboard data.");
            }

            downloadBlob(await response.blob(), "reporting_dashboard_report.xlsx");
        } catch (err) {
            setError(err.message || "Failed to export reporting dashboard data.");
        } finally {
            setExporting(false);
        }
    };

    const assets = dashboardData?.assets || {};
    const apiTopDowntimeAssets = assets.topAssetsByDowntime || [];
    const apiTopMaintenanceAssets = assets.topAssetsByMaintenanceCost || assets.topAssetsByMaintenance || [];
    const apiTopTurnoverTeams = dashboardData?.topTeamsByTurnover || [];

    const downtimeStats = [
        { value: formatCount(assets.problemsTotal), label: "Asset Problems" },
        { value: formatDuration(assets.downtimeAverageSeconds), label: "Average Downtime" },
        { value: formatDuration(assets.downtimeTotalSeconds), label: "Total Downtime" },
    ];

    const topDowntimeAssets = apiTopDowntimeAssets.map((asset) => ({
        id: asset.assetId,
        iccName: asset.assetName,
        value: `${formatCount(asset.problemCount)} problems (${formatDuration(asset.downtimeSeconds)} downtime)`,
    }));

    const maintenanceStats = [
        { value: formatCurrency(assets.maintenanceCostAverage), label: "Average Cost" },
        { value: formatCurrency(assets.maintenanceCostTotal), label: "Total Cost" },
    ];

    const topMaintenanceAssets = apiTopMaintenanceAssets.map((asset) => ({
        id: asset.assetId,
        iccName: asset.assetName,
        value: formatCurrency(asset.maintenanceCost || asset.maintenanceCostTotal || asset.cost),
    }));

    const turnoverStats = [
        {
            value: formatCount(dashboardData?.averageTurnover ?? getAverageTurnover(apiTopTurnoverTeams)),
            label: "Average Turnover",
        },
        { value: formatCount(dashboardData?.membersDeactivated), label: "Total Turnover" },
    ];

    const topTurnoverTeams = apiTopTurnoverTeams.map((team) => ({
        id: team.teamId,
        teamName: team.teamName,
        value: formatCount(team.deactivatedCount),
    }));

    const roadblockStats = [
        {
            value: formatCount(dashboardData?.averageTurnover),
            label: "Total Roadblocks",
        }
    ];

    const teamStaffing = dashboardData?.teamStaffing || {};
    const teamStaffingStats = [
        { value: `${formatCount(teamStaffing.totalStaffingPercentage)}%`, label: "Total Staffing" },
        { value: `${formatCount(teamStaffing.averageStaffingPercentage)}%`, label: "Average Staffing" },
    ];
    const topStaffingTeams = (teamStaffing.topTeams || []).map((t) => ({
        id: t.teamId,
        teamName: t.teamName,
        value: `${formatCount(t.staffingPercentage)}%`,
    }));
    const worstStaffingTeams = (teamStaffing.worstTeams || []).map((t) => ({
        id: t.teamId,
        teamName: t.teamName,
        value: `${formatCount(t.staffingPercentage)}%`,
    }));

    const worstTeamsReady = teamReadinessRanking
        .filter((t) => t.notReadyCount > 0)
        .slice(0, 10)
        .map((t) => ({
            id: t.id,
            name: t.name,
            value: `${(t.readyPercent * 100).toFixed(0)}%`,
        }));

    const bestTeamsReady = teamReadinessRanking
        .filter((t) => t.readyCount > 0)
        .slice()
        .reverse()
        .slice(0, 10)
        .map((t) => ({
            id: t.id,
            name: t.name,
            value: `${(t.readyPercent * 100).toFixed(0)}%`,
        }));

    return (
        <Container>
            <Grid columns={2} stackable>
                <Grid.Row>
                    <Grid.Column width={16}>
                        <Segment textAlign="left">
                            <Form size="small">
                                <Grid stackable>
                                    <Grid.Column mobile={16} tablet={16} computer={9}>
                                        <Form.Field>
                                            <label>Quick Range</label>
                                            <Button.Group size="small" className="reporting-dashboard__quick-range-buttons">
                                                {presetFilters.map((filter) => (
                                                    <Button
                                                        key={filter.key}
                                                        type="button"
                                                        color="blue"
                                                        basic={activePreset !== filter.key}
                                                        onClick={() => handlePresetClick(filter.key)}
                                                    >
                                                        {filter.label}
                                                    </Button>
                                                ))}
                                            </Button.Group>
                                        </Form.Field>
                                    </Grid.Column>
                                    <Grid.Column mobile={8} tablet={16} computer={7}>
                                        <Form.Group widths="equal">
                                            <Form.Input
                                                label="Start Date"
                                                type="date"
                                                value={startDate}
                                                onChange={(e) => {
                                                    setStartDate(e.target.value);
                                                    setActivePreset(null);
                                                }}
                                            />
                                            <Form.Input
                                                label="End Date"
                                                type="date"
                                                value={endDate}
                                                min={startDate || undefined}
                                                onChange={(e) => {
                                                    setEndDate(e.target.value);
                                                    setActivePreset(null);
                                                }}
                                            />
                                        </Form.Group>
                                    </Grid.Column>
                                    <Grid.Column mobile={16} tablet={16} computer={9}>
                                        <Form.Field>
                                            <label>Teams (optional)</label>
                                            <Dropdown
                                                placeholder={teamsLoading ? "Loading teams…" : "All teams"}
                                                fluid
                                                multiple
                                                selection
                                                search
                                                clearable
                                                options={teamOptions}
                                                value={selectedTeamIds}
                                                onChange={(_, { value }) => setSelectedTeamIds(value)}
                                                loading={teamsLoading}
                                                disabled={teamsLoading}
                                            />
                                        </Form.Field>
                                    </Grid.Column>
                                    <Grid.Column mobile={16} tablet={16} computer={16} textAlign="right">
                                        <Button
                                            basic
                                            loading={exporting}
                                            disabled={exporting || !startDate || !endDate}
                                            onClick={handleExport}
                                        >
                                            <Icon name="download" />
                                            Export
                                        </Button>
                                    </Grid.Column>
                                </Grid>
                            </Form>
                        </Segment>
                    </Grid.Column>
                </Grid.Row>
                {error && (
                    <Grid.Row>
                        <Grid.Column width={16}>
                            <Message negative>{error}</Message>
                        </Grid.Column>
                    </Grid.Row>
                )}
                <Grid.Row>
                    <Grid.Column>
                        <Segment textAlign="left">
                            <Header>Assets Downtime</Header>
                            {loading ? (
                                <StatisticPlaceholder count={downtimeStats.length} />
                            ) : (
                                <Statistic.Group widths={3} size="small" className="reporting-dashboard__stat-group">
                                    {downtimeStats.map((stat) => (
                                        <Statistic key={stat.label}>
                                            <Statistic.Value className="reporting-dashboard__stat-value">{stat.value}</Statistic.Value>
                                            <Statistic.Label className="reporting-dashboard__stat-label">{stat.label}</Statistic.Label>
                                        </Statistic>
                                    ))}
                                </Statistic.Group>
                            )}
                            <Header as="h4" dividing>Top 10 Assets by Downtime</Header>
                            {loading ? (
                                <AssetListPlaceholder count={topListPlaceholderCount} />
                            ) : (
                                <AssetList items={topDowntimeAssets} />
                            )}
                        </Segment>
                    </Grid.Column>
                    <Grid.Column>
                        <Segment textAlign="left">
                            <Header>Maintenance Cost</Header>
                            {loading ? (
                                <StatisticPlaceholder count={maintenanceStats.length} />
                            ) : (
                                <Statistic.Group widths={2} size="small" className="reporting-dashboard__stat-group">
                                    {maintenanceStats.map((stat) => (
                                        <Statistic key={stat.label}>
                                            <Statistic.Value className="reporting-dashboard__stat-value">{stat.value}</Statistic.Value>
                                            <Statistic.Label className="reporting-dashboard__stat-label">{stat.label}</Statistic.Label>
                                        </Statistic>
                                    ))}
                                </Statistic.Group>
                            )}
                            <Header as="h4" dividing>Top 10 Assets by Maintenance Cost</Header>
                            {loading ? (
                                <AssetListPlaceholder count={topListPlaceholderCount} />
                            ) : (
                                <AssetList items={topMaintenanceAssets} />
                            )}
                        </Segment>
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row className="reporting-dashboard__equal-height-row">
                    <Grid.Column>
                        <Segment textAlign="left">
                            <Header>Member Turnover</Header>
                            {loading ? (
                                <StatisticPlaceholder count={turnoverStats.length} />
                            ) : (
                                <Statistic.Group widths={2} size="small" className="reporting-dashboard__stat-group">
                                    {turnoverStats.map((stat) => (
                                        <Statistic key={stat.label}>
                                            <Statistic.Value className="reporting-dashboard__stat-value">{stat.value}</Statistic.Value>
                                            <Statistic.Label className="reporting-dashboard__stat-label">{stat.label}</Statistic.Label>
                                        </Statistic>
                                    ))}
                                </Statistic.Group>
                            )}
                            <Header as="h4" dividing>Top 10 Teams by Turnover</Header>
                            {loading ? (
                                <AssetListPlaceholder count={topListPlaceholderCount} />
                            ) : (
                                <TeamList items={topTurnoverTeams} />
                            )}
                        </Segment>
                    </Grid.Column>
                    <Grid.Column>
                        <Segment textAlign="left">
                            <Header>Roadblock Issues</Header>
                            {loading ? (
                                <StatisticPlaceholder count={roadblockStats.length} />
                            ) : (
                                <Statistic.Group widths={1} size="small" className="reporting-dashboard__stat-group">
                                    {roadblockStats.map((stat) => (
                                        <Statistic key={stat.label}>
                                            <Statistic.Value className="reporting-dashboard__stat-value">{stat.value}</Statistic.Value>
                                            <Statistic.Label className="reporting-dashboard__stat-label">{stat.label}</Statistic.Label>
                                        </Statistic>
                                    ))}
                                </Statistic.Group>
                            )}
                        </Segment>
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column width={16}>
                        <Segment textAlign="left">
                            <Header>Team Staffing</Header>
                            {loading ? (
                                <StatisticPlaceholder count={teamStaffingStats.length} />
                            ) : (
                                <Statistic.Group widths={2} size="small" className="reporting-dashboard__stat-group">
                                    {teamStaffingStats.map((stat) => (
                                        <Statistic key={stat.label}>
                                            <Statistic.Value className="reporting-dashboard__stat-value">{stat.value}</Statistic.Value>
                                            <Statistic.Label className="reporting-dashboard__stat-label">{stat.label}</Statistic.Label>
                                        </Statistic>
                                    ))}
                                </Statistic.Group>
                            )}
                            <Grid columns={2} stackable>
                                <Grid.Row>
                                    <Grid.Column>
                                        <Header as="h4" dividing>Top 10 Teams by Staffing</Header>
                                        {loading ? (
                                            <AssetListPlaceholder count={topListPlaceholderCount} />
                                        ) : (
                                            <TeamList items={topStaffingTeams} />
                                        )}
                                    </Grid.Column>
                                    <Grid.Column>
                                        <Header as="h4" dividing>Worst 10 Teams by Staffing</Header>
                                        {loading ? (
                                            <AssetListPlaceholder count={topListPlaceholderCount} />
                                        ) : (
                                            <TeamList items={worstStaffingTeams} />
                                        )}
                                    </Grid.Column>
                                </Grid.Row>
                            </Grid>
                        </Segment>
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column width={16}>
                        <Segment textAlign="left">
                            <Header>Readiness</Header>
                            <div style={{ paddingBottom: "1rem" }}>
                                <Menu pointing secondary>
                                    <MenuItem
                                        content={<><Icon color="grey" className="users" />Teams</>}
                                        name="team"
                                        onClick={() => setReadinessType("team")}
                                        active={readinessType === "team"}
                                    />
                                    <MenuItem
                                        content={<><Icon color="grey" className="truck" />Assets</>}
                                        name="asset"
                                        onClick={() => setReadinessType("asset")}
                                        active={readinessType === "asset"}
                                    />
                                    <MenuItem
                                        content={<><Icon color="grey" className="user" />Members</>}
                                        name="member"
                                        onClick={() => setReadinessType("member")}
                                        active={readinessType === "member"}
                                    />
                                </Menu>
                            </div>
                            {teamReadinessLoading ? (
                                <Placeholder fluid style={{marginBottom: "20px"}}>
                                    <Placeholder.Image style={{ height: 250 }} />
                                </Placeholder>
                            ) : teamReadinessTrend.length ? (
                                <ResponsiveContainer width="100%" height={250} margin={{bottom: 15}}>
                                    <LineChart data={teamReadinessTrend} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey="date"
                                            type="number"
                                            tickFormatter={(item) => new Date(item).toLocaleDateString()}
                                            domain={[
                                                teamReadinessTrend[0].date,
                                                teamReadinessTrend[teamReadinessTrend.length - 1].date,
                                            ]}
                                        />
                                        <YAxis allowDecimals={false} />
                                        <Tooltip labelFormatter={(v) => new Date(v).toLocaleDateString()} />
                                        <Legend />
                                        <Line animationDuration={150} type="monotone" dataKey="Ready" stroke="#00aaff" dot={teamReadinessTrend.length < 15} />
                                        <Line animationDuration={150} type="monotone" dataKey="Not ready" stroke="#ff0000" dot={teamReadinessTrend.length < 15} />
                                    </LineChart>
                                </ResponsiveContainer>
                            ) : (
                                <Message margin={{bottom: 15}}>No readiness data available.</Message>
                            )}
                            <Grid columns={2} stackable>
                                <Grid.Row>
                                    <Grid.Column>
                                        <Header as="h4" dividing>Worst % in Ready</Header>
                                        {teamReadinessLoading ? (
                                            <AssetListPlaceholder count={topListPlaceholderCount} />
                                        ) : (
                                            <ReadinessRankingList items={worstTeamsReady} type={readinessType} />
                                        )}
                                    </Grid.Column>
                                    <Grid.Column>
                                        <Header as="h4" dividing>Best % in Ready</Header>
                                        {teamReadinessLoading ? (
                                            <AssetListPlaceholder count={topListPlaceholderCount} />
                                        ) : (
                                            <ReadinessRankingList items={bestTeamsReady} type={readinessType} />
                                        )}
                                    </Grid.Column>
                                </Grid.Row>
                            </Grid>
                        </Segment>
                    </Grid.Column>
                </Grid.Row>
            </Grid>
        </Container>
    );
}
